import type { PatchCatalogEntry, PatchComplianceGroup, PatchSummary } from '../../api/client'
import { localeCorrente, t } from '../../i18n/lingua'
import { CountSeries, StatusStrip } from '../charts'
import { Card, EmptyState, StatCard } from '../common'
import { DataTable } from '../DataTable'
import { JobPhaseChip, KevChip, PriorityChip, SeverityChip, jobWhen, kindLabel, patchKindLabel, severityLabel } from './labels'
import type { WizardPrefill } from './NewJobWizard'

/**
 * The first section: is the fleet patched, where is it not, what to install
 * first, what is running. Every number opens the list it counts.
 */
export function Overview({ data, canWrite, onNewJob, onGo }: {
  data: PatchSummary
  canWrite: boolean
  onNewJob: (prefill?: WizardPrefill) => void
  onGo: (section: 'machines' | 'patches' | 'jobs', filter?: string) => void
}) {
  const total = data.compliance.total
  const pct = (n: number, of: number) => (of ? Math.round((n * 100) / of) : 100)
  const upToDate = pct(total.up_to_date, total.hosts)
  const withinSla = pct(total.within_sla, total.hosts)
  const notReady = data.no_agent + data.missing_tags
  const sla = data.sla_days

  if (!data.hosts) {
    return (
      <Card>
        <EmptyState
          title={t('Nessuna macchina da aggiornare, per ora.')}
          hint={t('Le macchine compaiono qui quando hanno l’agente CENSO o quando la valutazione di sicurezza trova patch mancanti. Installa l’agente dalla scheda di una macchina, o da Inventario → Agenti.')}
        />
      </Card>
    )
  }

  return (
    <>
      <div className="stats">
        <StatCard label={t('Macchine aggiornate')} value={`${upToDate}%`}
          hint={t('{n} su {m} senza patch mancanti', { n: total.up_to_date, m: total.hosts })}
          tone={upToDate >= 90 ? 'ok' : upToDate >= 60 ? 'warn' : 'error'}
          onClick={() => onGo('machines', 'missing')} />
        <StatCard label={t('Entro i tempi (SLA)')} value={`${withinSla}%`}
          hint={t('{n} macchine con patch oltre i tempi', { n: total.hosts - total.within_sla })}
          tone={withinSla >= 95 ? 'ok' : withinSla >= 75 ? 'warn' : 'error'}
          onClick={() => onGo('machines', 'overdue')} />
        <StatCard label={t('Patch mancanti')} value={data.patches}
          hint={t('{n} urgenti', { n: data.urgent })}
          tone={data.urgent ? 'error' : undefined}
          onClick={() => onGo('patches', data.urgent ? 'urgent' : undefined)} />
        <StatCard label={t('Sfruttate attivamente')} value={total.exploited}
          hint={t('patch per vulnerabilità usate negli attacchi (CISA KEV)')}
          tone={total.exploited ? 'error' : 'ok'}
          onClick={() => onGo('patches', 'exploited')} />
        <StatCard label={t('Da sistemare')} value={data.failed + data.needs_reboot}
          hint={t('{f} non riuscite, {r} da riavviare (ultimi 30 giorni)', { f: data.failed, r: data.needs_reboot })}
          tone={data.failed ? 'error' : data.needs_reboot ? 'warn' : undefined}
          onClick={() => onGo('jobs')} />
        <StatCard label={t('Non aggiornabili')} value={notReady}
          hint={t('{a} senza agente, {g} senza tag obbligatorio', { a: data.no_agent, g: data.missing_tags })}
          tone={notReady ? 'warn' : undefined}
          onClick={() => onGo('machines', 'not_ready')} />
      </div>

      <Card title={t('Per ambiente')}>
        <EnvironmentTable rows={data.compliance.by_environment} envKey={data.environment_key} />
        <p className="field__hint">
          {t('Tempi di installazione (SLA): critiche entro {c} giorni, alte {a}, medie {m}, basse {b}. Una patch oltre i tempi conta «fuori SLA».', {
            c: sla.critica ?? 15, a: sla.alta ?? 30, m: sla.media ?? 90, b: sla.bassa ?? 180,
          })}
        </p>
      </Card>

      <Card title={t('Da installare per prime')}
        actions={<button className="secondary" onClick={() => onGo('patches')}>{t('Tutte le patch')}</button>}>
        {data.top_patches.length === 0 ? (
          <EmptyState title={t('Nessuna patch mancante.')} />
        ) : (
          <TopPatches rows={data.top_patches} canWrite={canWrite} onNewJob={onNewJob} />
        )}
      </Card>

      <Card title={t('Interventi in corso')}
        actions={<button className="secondary" onClick={() => onGo('jobs')}>{t('Tutti gli interventi')}</button>}>
        {data.jobs.length === 0 ? (
          <EmptyState title={t('Nessun intervento in corso.')}
            hint={canWrite ? t('«Nuovo intervento» sceglie cosa installare, su quali macchine e quando.') : undefined} />
        ) : (
          <ul className="patch-jobs-mini">
            {data.jobs.map((j) => (
              <li key={j.id}>
                <button type="button" className="link" onClick={() => onGo('jobs', j.id)}>{j.name}</button>
                {' '}<JobPhaseChip phase={j.phase} />
                <span className="text-muted"> · {kindLabel(j.kind)} · {jobWhen(j)}</span>
                <ProgressStrip p={j.progress} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t('Andamento')}>
        {data.trend.length < 2 ? (
          <EmptyState title={t('L’andamento si costruisce da oggi: un punto al giorno.')} />
        ) : (
          <div className="patch-trend">
            <div>
              <h3 className="patch-trend__title">{t('Patch mancanti')}</h3>
              <CountSeries points={data.trend.map((p) => ({ date: p.day, value: p.missing }))}
                label={t('patch mancanti')} altezza={160} />
            </div>
            <div>
              <h3 className="patch-trend__title">{t('Macchine fuori SLA')}</h3>
              <CountSeries points={data.trend.map((p) => ({ date: p.day, value: p.hosts - p.within_sla }))}
                tone="warn" label={t('macchine fuori SLA')} altezza={160} />
            </div>
          </div>
        )}
      </Card>
    </>
  )
}

export function ProgressStrip({ p }: { p: PatchSummary['jobs'][number]['progress'] }) {
  return (
    <StatusStrip emptyHint={t('Nessuna macchina.')} data={[
      { key: 'applied', label: t('installate'), value: p.applied, tone: 'ok' },
      { key: 'needs_reboot', label: t('da riavviare'), value: p.needs_reboot, tone: 'warn' },
      { key: 'failed', label: t('non riuscite'), value: p.failed + p.blocked, tone: 'error' },
      { key: 'running', label: t('in corso'), value: p.running, tone: 'info' },
      { key: 'waiting', label: t('in attesa'), value: p.waiting, tone: 'info' },
    ]} />
  )
}

export function EnvironmentTable({ rows, envKey }: { rows: PatchComplianceGroup[]; envKey: string }) {
  const nf = (n: number) => n.toLocaleString(localeCorrente())
  return (
    <DataTable
      rows={rows}
      rowKey={(g) => g.environment}
      columns={[
        {
          key: 'environment', header: t('Ambiente'),
          value: (g) => (g.environment === '-' ? t('senza «{k}»', { k: envKey }) : g.environment),
          render: (g) => (g.environment === '-' ? <span className="text-muted">{t('senza «{k}»', { k: envKey })}</span> : g.environment),
        },
        { key: 'hosts', header: t('Macchine'), type: 'number', render: (g) => nf(g.hosts) },
        {
          key: 'up_to_date', header: t('Aggiornate'), type: 'number', align: 'left',
          value: (g) => quota(g.up_to_date, g.hosts), render: (g) => <PercentBar n={g.up_to_date} of={g.hosts} />,
        },
        {
          key: 'within_sla', header: t('Entro SLA'), type: 'number', align: 'left',
          value: (g) => quota(g.within_sla, g.hosts), render: (g) => <PercentBar n={g.within_sla} of={g.hosts} />,
        },
        { key: 'missing', header: t('Patch mancanti'), type: 'number', render: (g) => nf(g.missing) },
        {
          key: 'critical', header: t('Critiche'), type: 'number',
          render: (g) => (g.critical ? <strong className="text-error">{nf(g.critical)}</strong> : 0),
        },
        {
          key: 'exploited', header: t('Sfruttate'), type: 'number',
          render: (g) => (g.exploited ? <strong className="text-error">{nf(g.exploited)}</strong> : 0),
        },
        { key: 'end_of_life', header: t('Fuori supporto'), type: 'number', render: (g) => g.end_of_life || '' },
      ]}
    />
  )
}

/** The share PercentBar shows, as a number for sorting and filtering. */
function quota(n: number, of: number): number {
  return of ? Math.round((n * 100) / of) : 100
}

/** A share as a short bar with the number written next to it. */
export function PercentBar({ n, of }: { n: number; of: number }) {
  const pct = of ? Math.round((n * 100) / of) : 100
  const tone = pct >= 90 ? 'ok' : pct >= 60 ? 'warn' : 'error'
  return (
    <span className="patch-pct" title={t('{n} su {m}', { n, m: of })}>
      <span className="patch-pct__track"><span className={`patch-pct__fill patch-pct__fill--${tone}`} style={{ width: `${pct}%` }} /></span>
      <span className="patch-pct__value">{pct}%</span>
    </span>
  )
}

function TopPatches({ rows, canWrite, onNewJob }: {
  rows: PatchCatalogEntry[]; canWrite: boolean; onNewJob: (prefill?: WizardPrefill) => void
}) {
  return (
    <DataTable
      rows={rows}
      rowKey={(p) => p.key}
      conteggio={{ nome: t('patch') }}
      columns={[
        {
          key: 'priority', header: t('Priorità'), value: (p) => p.priority,
          render: (p) => <PriorityChip priority={p.priority_label} />, filter: false,
        },
        {
          key: 'name', header: t('Patch'), value: (p) => [p.name, patchKindLabel(p.kind), p.title].filter(Boolean).join(' '),
          render: (p) => (
            <>
              <span className="mono">{p.name}</span> {p.exploited && <KevChip />}
              <div className="text-muted patch-sub">{patchKindLabel(p.kind)}{p.title && p.title !== p.name ? ` · ${p.title}` : ''}</div>
            </>
          ),
        },
        {
          key: 'severity', header: t('Gravità'), value: (p) => severityLabel(p.severity),
          render: (p) => <SeverityChip severity={p.severity} />,
        },
        { key: 'hosts', header: t('Macchine'), type: 'number', value: (p) => p.host_count },
        {
          key: 'age', header: t('Da'), type: 'number', align: 'left', value: (p) => p.age_days,
          render: (p) => (
            <>
              {t('{n} giorni', { n: p.age_days })}
              {p.overdue && <> <span className="chip patch-chip patch-chip--warn">{t('oltre SLA')}</span></>}
            </>
          ),
        },
        {
          key: 'action', header: '', sort: false, filter: false, searchable: false,
          render: (p) => (canWrite && p.installable ? (
            <button className="link" onClick={() => onNewJob({
              kind: 'selected', patchKeys: [p.key], ciIds: p.hosts.map((h) => h.ci_id),
              name: t('Installa {p}', { p: p.name }),
            })}>{t('Installa')}</button>
          ) : null),
        },
      ]}
    />
  )
}
